import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { RefreshCw, AlertTriangle } from "lucide-react";

/**
 * BrokerHealthStrip — compact always-visible IBKR MCP status row for the Dashboard.
 * Deep-links to Portfolio for the full panel and to Settings for connect/disconnect.
 */
export default function BrokerHealthStrip() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [drafts, setDrafts] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api.get("/ibkr/mcp/status");
      setStatus(s.data);
      if (s.data?.connected) {
        const [sum, dr] = await Promise.allSettled([
          api.get("/ibkr/mcp/summary"),
          api.get("/ibkr/mcp/drafts"),
        ]);
        if (sum.status === "fulfilled") setSummary(sum.value.data);
        if (dr.status === "fulfilled") setDrafts(dr.value.data?.count || 0);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (loading && !status) return null;

  const connected = !!status?.connected;
  const netLiq =
    summary?.summary?.net_liquidation ??
    summary?.summary?.netLiquidation ??
    summary?.summary?.equity_with_loan_value ??
    null;

  if (!connected) {
    return (
      <Link to="/settings" data-testid="broker-health-strip"
            className="block rounded-xl border border-border/60 hover:border-emerald-500/40 bg-secondary/20 px-3 py-2 transition-colors">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-md bg-secondary flex items-center justify-center text-[10px] font-bold shrink-0">IBKR</div>
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">Live broker not connected</div>
              <div className="text-[10px] text-muted-foreground truncate">Connect IBKR for 1-tap live trades →</div>
            </div>
          </div>
          <span className="text-[10px] font-mono-tab uppercase tracking-wider text-muted-foreground">Setup</span>
        </div>
      </Link>
    );
  }

  return (
    <div data-testid="broker-health-strip"
         className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] px-3 py-2">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
              IBKR
              <span className={`text-[10px] font-mono-tab uppercase tracking-wider ${
                status?.account_type === "paper"
                  ? "text-amber-400"
                  : status?.account_type === "live"
                    ? "text-emerald-400"
                    : "text-muted-foreground"
              }`}>
                {status?.account_type === "paper" ? "· Paper" : status?.account_type === "live" ? "· Live" : "· Connected"}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground truncate">
              Net Liq {netLiq != null ? <span className="font-mono-tab text-foreground">{fmtMoney(netLiq)}</span> : "—"}
              {drafts > 0 && (
                <>
                  {" · "}
                  <span className="text-amber-400 font-semibold">{drafts} pending draft{drafts > 1 ? "s" : ""}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {drafts > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-amber-400 font-medium mr-1">
              <AlertTriangle className="w-3 h-3" />
              <span className="hidden sm:inline">confirm in IBKR</span>
            </div>
          )}
          <button onClick={load} data-testid="broker-strip-refresh"
                  className="p-1 rounded-md hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
          <Link to="/portfolio" data-testid="broker-strip-portfolio"
                className="text-[10px] font-mono-tab uppercase tracking-wider text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded-md hover:bg-emerald-500/10 transition-colors">
            View →
          </Link>
        </div>
      </div>
    </div>
  );
}
